'use client';

import {
  Bot,
  CheckCircle2,
  DatabaseZap,
  FileText,
  Gauge,
  Network,
  RefreshCw,
  Send,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '~/components/ui/button';

interface HeroSectionProps {
  onQuotePrefill: (notes: string) => void;
}

const serviceChips = ['Data integration & sync', 'Workflow automation', 'AI & MCP integrations', 'Websites & hosting'];
const trustItems = ['Honest scope', 'Custom software', 'Flexible deployments'];

const nodes = [
  { label: 'CRM', detail: 'Zoho, HubSpot, Salesforce', className: 'left-[4%] top-[18%]', icon: DatabaseZap },
  { label: 'Field Ops', detail: 'Housecall Pro, Jobber', className: 'right-[3%] top-[20%]', icon: Gauge },
  { label: 'AI & MCP', detail: 'Agents, safe tool access', className: 'left-[1%] bottom-[24%]', icon: Network },
  { label: 'Reports', detail: 'Dashboards & exports', className: 'right-[7%] bottom-[22%]', icon: FileText },
  { label: 'Automations', detail: 'Queues, alerts, syncs', className: 'left-[34%] bottom-[2%]', icon: Workflow },
];

// The mobile visual mirrors the same nodes in the same order; only the placement differs.
const mobileNodePositions = [
  'left-0 top-[25%]',
  'right-0 top-[27%]',
  'left-0 bottom-[28%]',
  'right-0 bottom-[27%]',
  'left-1/2 bottom-[9%] -translate-x-1/2',
];
const mobileNodes = nodes.map((node, index) => ({ ...node, className: mobileNodePositions[index] }));

const hub = { title: 'SYNC', caption: 'data hub' };
const pills = { primary: 'Two-way sync', secondary: 'AI where it helps' };

export default function HeroSection({ onQuotePrefill }: HeroSectionProps) {
  const [selectedServices, setSelectedServices] = useState<string[]>(['Data integration & sync']);
  const [projectNeed, setProjectNeed] = useState('');

  const toggleService = (service: string) => {
    setSelectedServices((current) =>
      current.includes(service) ? current.filter((item) => item !== service) : [...current, service],
    );
  };

  const startQuote = () => {
    const parts = [
      selectedServices.length ? `Services: ${selectedServices.join(', ')}` : '',
      projectNeed ? `Project need: ${projectNeed}` : '',
    ].filter(Boolean);
    onQuotePrefill(parts.join('\n\n'));
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative isolate overflow-hidden bg-[var(--color-ink)] text-[var(--color-paper)]">
      <div className="absolute inset-0 hero-grid opacity-45" />
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[var(--color-canvas)] to-transparent" />
      <div className="container relative grid items-center gap-10 px-4 py-10 md:px-6 lg:grid-cols-[0.92fr_1.08fr] lg:gap-12 lg:py-20">
        <div className="max-w-3xl">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#b76e3c]/35 bg-[#fffdf7]/8 px-3 py-1 text-sm text-[#f7f3ea]">
            <Sparkles className="h-4 w-4 text-[var(--color-copper)]" />
            Small shop. Senior-level data integration, sync, and AI work.
          </div>
          <h1 className="max-w-4xl text-[2.55rem] font-semibold leading-[0.98] tracking-normal text-balance sm:text-6xl lg:text-[4.45rem]">
            Connect, sync, and automate your business
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-[#f7f3ea]/82 md:text-xl">
            Data integration, synchronization and workflow automation. We're an AI first company with experience
            building MCPs, websites, integration and synchronization software. For small & medium sized businesses that
            need their tools to work together.
          </p>

          <div className="mt-9 rounded-lg border border-[#d8cdbb]/20 bg-[var(--color-paper)] p-4 text-[var(--color-ink)] shadow-2xl shadow-black/25">
            <div className="flex flex-wrap gap-2">
              {serviceChips.map((service) => {
                const isSelected = selectedServices.includes(service);
                return (
                  <button
                    key={service}
                    type="button"
                    onClick={() => toggleService(service)}
                    className={`rounded-full border px-3 py-2 text-sm font-medium transition ${
                      isSelected
                        ? 'border-[var(--color-signal)] bg-[var(--color-signal)] text-white'
                        : 'border-[var(--color-line)] bg-white text-[var(--color-muted)] hover:border-[var(--color-signal)]'
                    }`}
                  >
                    {service}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
              <input
                value={projectNeed}
                onChange={(event) => setProjectNeed(event.target.value)}
                className="h-12 rounded-md border border-[var(--color-line)] bg-white px-4 text-base outline-none transition placeholder:text-[#625b50]/65 focus:border-[var(--color-signal)] focus:ring-2 focus:ring-[#2f6f73]/20"
                placeholder="What needs to connect, automate, or sync?"
              />
              <Button
                type="button"
                onClick={startQuote}
                className="h-12 rounded-md bg-[var(--color-copper)] px-5 text-base font-semibold text-white hover:bg-[var(--color-copper-dark)]"
              >
                <Send className="mr-2 h-4 w-4" />
                Start quote
              </Button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2 text-center text-xs font-medium text-[#f7f3ea] sm:flex sm:flex-wrap sm:gap-x-3 sm:text-sm">
            {trustItems.map((item) => (
              <span
                key={item}
                className="inline-flex min-h-10 items-center justify-center gap-1 rounded-md border border-[#d8cdbb]/16 bg-[#fffdf7]/7 px-2 py-2"
              >
                <CheckCircle2 className="hidden h-3.5 w-3.5 text-[var(--color-copper)] sm:block" />
                {item}
              </span>
            ))}
          </div>
        </div>

        <AiOrchestrationVisual />
      </div>
    </section>
  );
}

function AiOrchestrationVisual() {
  return (
    <>
      <MobileAiOrchestrationVisual />
      <div className="relative mx-auto hidden aspect-[1.04] w-full max-w-[640px] md:block">
        <div className="absolute left-1/2 top-[4%] z-20 w-[230px] -translate-x-1/2 rounded-lg border border-[#b76e3c]/35 bg-[var(--color-paper)] px-4 py-3 text-center text-[var(--color-ink)] shadow-xl">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-copper)]">
            Orchestrated by
          </div>
          <div className="mt-1 text-xl font-semibold">DataConnector-Pro</div>
        </div>

        <div className="absolute left-1/2 top-1/2 z-20 grid h-44 w-44 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-[#2f6f73]/50 bg-[var(--color-paper)] text-[var(--color-ink)] shadow-[0_0_60px_rgba(47,111,115,0.28)]">
          <div className="absolute inset-[-14px] rounded-full border border-dashed border-[#b76e3c]/55 motion-safe:animate-[spin_18s_linear_infinite]" />
          <div className="text-center">
            <RefreshCw className="mx-auto h-10 w-10 text-[var(--color-signal)]" />
            <div className="mt-2 text-3xl font-semibold">{hub.title}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--color-muted)]">{hub.caption}</div>
          </div>
        </div>

        <svg className="absolute inset-0 z-0 h-full w-full" viewBox="0 0 640 640" fill="none" aria-hidden="true">
          <path className="data-path data-path-a" d="M320 118 C320 190 320 214 320 252" />
          <path className="data-path data-path-b" d="M168 196 C220 232 248 254 284 286" />
          <path className="data-path data-path-c" d="M478 202 C424 232 394 254 356 286" />
          <path className="data-path data-path-d" d="M142 466 C214 418 248 392 286 354" />
          <path className="data-path data-path-e" d="M506 466 C434 418 398 392 354 354" />
          <path className="data-path data-path-f" d="M320 594 C320 510 320 442 320 388" />
        </svg>

        <div className="absolute inset-16 rounded-full border border-[#d8cdbb]/12" />
        <div className="absolute inset-4 rounded-full border border-[#d8cdbb]/8" />

        {nodes.map(({ label, detail, className, icon: Icon }) => (
          <div
            key={label}
            className={`absolute z-10 w-[180px] rounded-lg border border-[#d8cdbb]/35 bg-[var(--color-paper)] p-3 text-[var(--color-ink)] shadow-xl ${className}`}
          >
            <div className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-md bg-[var(--color-canvas)] text-[var(--color-signal)]">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold">{label}</div>
                <div className="text-xs text-[var(--color-muted)]">{detail}</div>
              </div>
            </div>
          </div>
        ))}

        <div className="absolute right-[24%] top-[38%] z-30 rounded-full border border-[#b76e3c]/35 bg-[var(--color-copper)] px-3 py-1 text-xs font-semibold text-white shadow-lg motion-safe:animate-pulse">
          {pills.primary}
        </div>
        <div className="absolute left-[24%] top-[58%] z-30 rounded-full border border-[#2f6f73]/35 bg-[var(--color-signal)] px-3 py-1 text-xs font-semibold text-white shadow-lg motion-safe:animate-pulse">
          {pills.secondary}
        </div>
        <Bot className="absolute bottom-[13%] right-[34%] z-30 h-6 w-6 text-[var(--color-copper)]" />
      </div>
    </>
  );
}

function MobileAiOrchestrationVisual() {
  return (
    <div className="relative mx-auto h-[392px] w-full min-w-[328px] max-w-[360px] overflow-visible md:hidden">
      <div className="absolute left-1/2 top-[2%] z-20 w-[176px] -translate-x-1/2 rounded-md border border-[#b76e3c]/35 bg-[var(--color-paper)] px-3 py-2.5 text-center text-[var(--color-ink)] shadow-xl">
        <div className="text-[0.56rem] font-semibold uppercase tracking-[0.18em] text-[var(--color-copper)]">
          Orchestrated by
        </div>
        <div className="mt-0.5 text-base font-semibold">DataConnector-Pro</div>
      </div>

      <svg
        className="absolute inset-x-0 top-0 z-0 h-[360px] w-full"
        viewBox="0 0 360 360"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="180" cy="196" r="132" stroke="rgba(216,205,187,0.3)" strokeWidth="1" />
        <circle cx="180" cy="196" r="96" stroke="rgba(216,205,187,0.22)" strokeWidth="1" />
        <path className="data-path data-path-a" d="M180 78 C180 118 180 132 180 148" />
        <path className="data-path data-path-b" d="M84 128 C116 148 134 160 154 176" />
        <path className="data-path data-path-c" d="M278 132 C242 150 224 162 204 178" />
        <path className="data-path data-path-d" d="M82 266 C118 238 136 224 154 208" />
        <path className="data-path data-path-e" d="M282 268 C244 240 224 224 204 208" />
        <path className="data-path data-path-f" d="M180 322 C180 276 180 244 180 226" />
      </svg>

      <div className="absolute left-1/2 top-[54%] z-20 grid h-28 w-28 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-[#2f6f73]/50 bg-[var(--color-paper)] text-[var(--color-ink)] shadow-[0_0_46px_rgba(47,111,115,0.35)]">
        <div className="absolute inset-[-9px] rounded-full border border-dashed border-[#b76e3c]/55 motion-safe:animate-[spin_18s_linear_infinite]" />
        <div className="text-center">
          <RefreshCw className="mx-auto h-7 w-7 text-[var(--color-signal)]" />
          <div className="mt-1 text-xl font-semibold leading-none">{hub.title}</div>
          <div className="mt-1 text-[0.58rem] uppercase tracking-[0.16em] text-[var(--color-muted)]">{hub.caption}</div>
        </div>
      </div>

      {mobileNodes.map(({ label, className, icon: Icon }) => (
        <div
          key={label}
          className={`absolute z-10 w-[128px] rounded-md border border-[#d8cdbb]/35 bg-[var(--color-paper)] p-2 text-[var(--color-ink)] shadow-xl ${className}`}
        >
          <div className="flex items-center gap-1.5">
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded bg-[var(--color-canvas)] text-[var(--color-signal)]">
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 whitespace-nowrap text-xs font-semibold leading-tight">{label}</div>
          </div>
        </div>
      ))}

      <div className="absolute inset-x-0 bottom-0 z-30 flex justify-center gap-3 pt-4">
        <span className="rounded-full bg-[var(--color-copper)] px-2.5 py-1 text-[0.62rem] font-semibold text-white shadow-lg motion-safe:animate-pulse">
          {pills.primary}
        </span>
        <span className="rounded-full bg-[var(--color-signal)] px-2.5 py-1 text-[0.62rem] font-semibold text-white shadow-lg motion-safe:animate-pulse">
          {pills.secondary}
        </span>
      </div>
      <Bot className="absolute bottom-[15%] right-[7%] z-30 h-5 w-5 text-[var(--color-copper)]" />
    </div>
  );
}
